import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';

import { SEO } from '../../components/seo';

import { WrappedMenu } from '../../components/new/Menu';
import { Footer } from '../../components/new/Footer';
import { mobileMQ } from '../../components/new/media-utils';
import { CustomBreadCrumbs } from '../../components/new/CustomBreadCrumbs';

import leadImage from '../../images/welcome-teachers/lead.png';
import laptopIcon from '../../images/welcome-teachers/laptop.svg';
import capIcon from '../../images/welcome-teachers/graduation-cap.svg';
import moneyIcon from '../../images/welcome-teachers/money.svg';
import externalLinkIcon from '../../images/welcome-teachers/external-link.svg';
import { Container } from '../../components/Container';
import { Wrap } from '../../components/new/Wrap';
import { BaseHeadingStyle } from '../../components/new/headers';
import { PageWrapper, ContentWrapper } from '../../components/new/wrappers';

const BaseTextStyle = styled.div`
    font-family: 'Circe Web';
    font-size: 22px;
    line-height: 140%;
    color: #404040;

    a {
        color: #0b9cdb;

        &:hover {
            text-decoration: underline;
        }
    }

    ${mobileMQ(css`
        font-size: 18px;
        line-height: 150%;
    `)}
`;

const BaseListStyles = styled.ul`
    font-family: 'Circe Web';
    font-size: 22px;
    line-height: 140%;
    color: #404040;
    padding: 0 20px;
    box-sizing: border-box;

    li {
        margin-top: 0;
        margin-bottom: 0;
    }

    ${mobileMQ(css`
        font-size: 18px;
        line-height: 150%;
    `)}
`;

const LeadImage = styled.img`
    max-width: 100%;
    height: auto;
    margin-top: -50px;
    max-height: 400px;

    ${mobileMQ(css`
        margin-top: 0;
    `)}
`;

const H1 = styled(BaseHeadingStyle)`
    font-size: 80px;
    line-height: 100%;
    margin: 48px 0;
    position: relative;
    left: -3px;
    max-width: 980px;

    @media screen and (max-width: 760px) {
        font-size: 38px;
        margin: 24px 0 16px;
    }
`;

const H2 = styled(BaseHeadingStyle)`
    font-size: 48px;
    line-height: 100%;
    margin: 48px 0 16px;

    @media screen and (max-width: 760px) {
        font-size: 28px;
        margin-top: 32px;
    }
`;

const Section = styled.div`
    position: relative;

    @media screen and (max-width: 1024px) {
        &.certificate,
        &.schedule,
        &.money {
            &:before {
                content: '';
                display: block;
                width: 150px;
                height: 120px;
                background-repeat: no-repeat;
                background-position: 44px 0;
                background-size: cover;
                position: absolute;
                top: 0;
                right: -24px;
                z-index: -1;
                opacity: 0.2;
            }
        }

        &.certificate:before {
            background-image: url(${capIcon});
        }

        &.schedule:before {
            background-image: url(${laptopIcon});
        }

        &.money:before {
            background-image: url(${moneyIcon});
        }
    }
`;

const SectionTitle = styled(BaseHeadingStyle)`
    font-family: 'Circe Web';
    font-size: 22px;
    line-height: 140%;
    margin: 32px 0 8px;

    @media screen and (max-width: 760px) {
        font-size: 18px;
        line-height: 150%;
        margin-top: 16px;
    }
`;

const Text = styled(BaseTextStyle)`
    margin: 0;

    & + & {
        margin-top: 32px;
    }
`;

const CondensedText = styled(BaseTextStyle)`
    margin: 0;
    max-width: 864px;

    @media screen and (max-width: 760px) {
        font-size: 20px;
        line-height: 160%;
    }
`;

const UnorderedList = styled(BaseListStyles)`
    margin-bottom: 16px;
    list-style: none;

    li::before {
        content: '•';
        color: inherit;
        display: inline-block;
        width: 20px;
        margin-left: -20px;
        text-align: center;
    }
`;

const HighlightedText = styled(BaseTextStyle)`
    width: 100%;
    background-color: #ccf1fe;
    border-radius: 16px;
    padding: 24px 32px 32px;
    box-sizing: border-box;
    margin: 16px 0 24px;

    @media screen and (max-width: 760px) {
        padding: 24px;
        margin: 16px 0;
    }
`;

const Aside = styled.div`
    position: relative;
    left: 100%;
    width: 336px;
    display: block;
    padding-left: 144px;
    box-sizing: border-box;

    .aside-icon {
        height: 64px;
        width: auto;
        margin: -40px 0 8px;
    }

    .aside-text {
        font-family: 'Circe Web';
        color: #404040;
        font-size: 18px;
        line-height: 120%;
    }

    .aside-price {
        position: relative;
        top: -100px;
    }

    .aside-price__amount {
        font-weight: 700;
        font-family: 'Futura PT', sans-serif;
        font-size: 76px;
        line-height: 120px;
        color: #ff9c9f;
    }

    .aside-price__period {
        font-size: 24px;
        line-height: 120%;
        font-family: 'Circe Web';
        font-weight: 700;
        position: absolute;
        right: 0;
        bottom: 20px;
    }

    @media screen and (max-width: 1024px) {
        display: none;
    }
`;

const AsideNote = styled.div`
    position: absolute;
    top: 0;

    &.aside_get-to {
        top: 36px;
    }

    &.aside_translate {
        top: 63px;
    }

    &.aside_screenrecord {
        top: 154px;

        .aside-link {
            color: #ff9c9f;

            &:before {
                content: '';
                background-image: url(${externalLinkIcon});
                background-repeat: no-repeat;
                background-size: 13px 13px;
                display: inline-block;
                width: 13px;
                height: 13px;
                margin-left: -21px;
                padding-right: 8px;
            }
        }
    }

    &.aside_form {
        top: 74px;
    }

    .aside-link {
        font-family: 'Circe Web';
        color: #000;
        font-size: 18px;
        line-height: 150%;
        text-decoration: none;
        border-bottom: 1px solid;
    }
`;

const TeachersPage = ({ pageContext }) => {
    const {
        breadcrumb: { crumbs },
    } = pageContext;

    return (
        <Wrap>
            <SEO title="Учителям" description="КЭСПА для преподавателей" />
            <Container>
                <WrappedMenu />
                <LeadImage src={leadImage} />
                <PageWrapper>
                    <H1 as="h1">КЭСПА для преподавателей</H1>
                    <CondensedText as="p">
                        Привет! Вы&nbsp;здесь, потому что хотите преподавать
                        с&nbsp;КЭСПА, не&nbsp;так&nbsp;ли? Давайте знакомиться.
                        Мы&nbsp;КЭСПА&nbsp;&mdash; онлайн-школа английского
                        языка. Мы&nbsp;отличаемся от&nbsp;других школ, наша
                        особенность&nbsp;&mdash; это наша методика. Она
                        позволяет нашим ученикам быстрее заговорить
                        на&nbsp;английском языке и&nbsp;заставляет это делать
                        даже тех, кто уже давно отчаялся! Все преподаватели
                        в&nbsp;нашей школе проходят обязательную стажировку
                        на&nbsp;старте, обычно она занимает 2&nbsp;недели. Чтобы
                        попасть на&nbsp;нее, мы&nbsp;попросим вас сделать
                        небольшое тестовое задание (45&nbsp;мин).
                        Но&nbsp;сперва:
                    </CondensedText>
                    <ContentWrapper>
                        <H2 as="h2">Что мы предлагаем?</H2>

                        <Section className="certificate">
                            <SectionTitle>
                                Стажировка и сертификат КЭСПА
                            </SectionTitle>
                            <Aside>
                                <AsideNote>
                                    <img src={capIcon} class="aside-icon" />
                                    <div class="aside-text">
                                        Учим и&nbsp;сертифицируем
                                    </div>
                                </AsideNote>
                            </Aside>
                            <Text as="p">
                                После успешного прохождения стажировки
                                мы&nbsp;пригласим вас на&nbsp;работу
                                в&nbsp;КЭСПА. Мы&nbsp;уверены, что стажировка
                                позволит вам по-новому взглянуть
                                на&nbsp;преподавание английского языка. После
                                того как вы&nbsp;проведете достаточное
                                количество занятий в&nbsp;нашей школе,
                                мы&nbsp;выдадим вам именной сертификат,
                                подтверждающий, что вы&nbsp;владеете методикой
                                КЭСПА.
                            </Text>
                        </Section>

                        <Section className="money">
                            <SectionTitle>Хорошая зарплата</SectionTitle>
                            <Aside>
                                <AsideNote>
                                    <img src={moneyIcon} class="aside-icon" />
                                    <div class="aside-text">
                                        Платим вовремя <br />
                                        Не&nbsp;штрафуем <br />
                                        Повышаем ставку
                                    </div>
                                </AsideNote>
                            </Aside>
                            <Text as="p">
                                Сразу после прохождения стажировки
                                мы&nbsp;предлагаем базовую ставку 550 рублей
                                за&nbsp;50-минутный урок. Именно столько
                                вы&nbsp;получите, если
                                вы&nbsp;самозанятый&nbsp;&mdash; налог&nbsp;6%
                                мы&nbsp;берем на&nbsp;себя. Мы&nbsp;платим
                                бонусы и&nbsp;увеличиваем ставку преподавателям,
                                которые долго с&nbsp;нами сотрудничают или чьи
                                ученики показывают феноменальные результаты.
                                Мы&nbsp;не&nbsp;штрафуем и&nbsp;платим вовремя.
                                При средней нагрузке преподаватель в&nbsp;КЭСПА
                                получает 60&nbsp;000 рублей в&nbsp;месяц.
                            </Text>
                            <Aside>
                                <AsideNote>
                                    <div class="aside-price">
                                        <div class="aside-price__amount">
                                            550₽
                                        </div>
                                        <div class="aside-price__period">
                                            в час
                                        </div>
                                    </div>
                                </AsideNote>
                            </Aside>
                        </Section>

                        <HighlightedText>
                            Вам нравится методика, хочется у&nbsp;нас
                            преподавать, но&nbsp;рассчитываете на&nbsp;большее
                            вознаграждение? Давайте пообщаемся! Мы&nbsp;ищем
                            преподавателей, которые готовы обучать учеников
                            с&nbsp;высоким уровнем знания языка.
                        </HighlightedText>

                        <Section className="schedule">
                            <SectionTitle>Удобный график</SectionTitle>
                            <Aside>
                                <AsideNote>
                                    <img src={laptopIcon} class="aside-icon" />
                                    <div class="aside-text">
                                        Нужен только компьютер и&nbsp;интернет
                                    </div>
                                </AsideNote>
                            </Aside>
                            <Text as="p">
                                Проводите уроки когда и&nbsp;откуда удобно.
                                Преподаватели в&nbsp;КЭСПА сами указывают свои
                                рабочие часы и&nbsp;получают учеников
                                в&nbsp;соответствии с&nbsp;ними.
                            </Text>
                        </Section>

                        <H2 as="h2">Как к нам попасть?</H2>
                        <Aside>
                            <AsideNote className="aside_get-to">
                                <Link to="/methodology" class="aside-link">
                                    Особенности методики КЭСПА
                                </Link>
                            </AsideNote>
                        </Aside>
                        <Text as="p">
                            Перед тем как начать выполнять тестовое задание
                            и&nbsp;стажироваться, пожалуйста, прочитайте про{' '}
                            <Link to="/methodology">
                                Особенности методики КЭСПА
                            </Link>
                            . Это описание принципов, на&nbsp;которые
                            мы&nbsp;опираемся в&nbsp;процессе обучения
                            в&nbsp;КЭСПА. Убедитесь, что вы&nbsp;поняли суть
                            метода и&nbsp;готовы работать с&nbsp;ним. Практика
                            показывает, что методика позволяет существенно
                            быстрее заговорить ученикам, а&nbsp;преподавателям
                            ощутимо меньше тратить времени на&nbsp;подготовку
                            к&nbsp;занятиям и&nbsp;проверку домашнего задания
                            в&nbsp;сравнении с&nbsp;классическими методами.
                        </Text>

                        <H2 as="h2">Тестовое задание</H2>

                        <Aside>
                            <AsideNote className="aside_translate">
                                <Link
                                    to="/welcome-teachers/testing-text"
                                    class="aside-link"
                                >
                                    Текст для перевода
                                </Link>
                            </AsideNote>
                        </Aside>

                        <Aside>
                            <AsideNote className="aside_screenrecord">
                                <a
                                    href="https://www.cam-recorder.com/"
                                    class="aside-link"
                                >
                                    Сервис для записи видео
                                </a>
                            </AsideNote>
                        </Aside>

                        <Text as="p">
                            Поскольку КЭСПА&nbsp;&mdash; языковая школа,
                            и&nbsp;в&nbsp;первую очередь, мы&nbsp;учим говорить,
                            нам очень важно увидеть и&nbsp;услышать вас. Поэтому
                            в&nbsp;качестве тестового задания мы&nbsp;просим вас
                            устно перевести текст{' '}
                            <Link to="/welcome-teachers/testing-text">
                                &laquo;Где учить иностранный язык,
                                в&nbsp;аудитории или на&nbsp;улице?&raquo;
                            </Link>{' '}
                            на&nbsp;камеру и&nbsp;выложить видео на&nbsp;YouTube
                            (вы&nbsp;можете сделать его доступным только
                            по&nbsp;прямой ссылке; если у&nbsp;вас нет аккаунта
                            Google, пожалуйста, заведите, он&nbsp;вам
                            понадобится в&nbsp;любом случае).
                            По&nbsp;возможности, запишите видео в&nbsp;тех
                            условиях, в&nbsp;которых вы&nbsp;преподаете или
                            собираетесь преподавать.
                        </Text>

                        <Aside>
                            <AsideNote className="aside_form">
                                <a
                                    href="https://docs.google.com/forms/d/e/1FAIpQLSeJQ_sDJM2vawGfrKfkOZtLYo4R7cv_4BIQ8MncXbLyHIbBWA/viewform"
                                    class="aside-link"
                                >
                                    Форма для связи с&nbsp;нами
                                </a>
                            </AsideNote>
                        </Aside>

                        <HighlightedText>
                            После того, как вы&nbsp;записали видео
                            и&nbsp;загрузили его на&nbsp;YouTube, пожалуйста,
                            заполните{' '}
                            <a href="https://docs.google.com/forms/d/e/1FAIpQLSeJQ_sDJM2vawGfrKfkOZtLYo4R7cv_4BIQ8MncXbLyHIbBWA/viewform">
                                нашу форму
                            </a>
                            . Помимо ссылки на&nbsp;ваше видео, мы&nbsp;также
                            просим указать ваши контактные данные и&nbsp;немного
                            рассказать о&nbsp;себе в&nbsp;свободной форме.
                        </HighlightedText>

                        <Text as="p">
                            В&nbsp;течение трех дней мы&nbsp;обязательно
                            вернемся к&nbsp;вам с&nbsp;обратной связью. Если
                            тестовое задание выполнено успешно, мы&nbsp;позовем
                            вас на&nbsp;онлайн-собеседование и&nbsp;предложим
                            пройти стажировку, которая состоит
                            из&nbsp;самостоятельной работы с&nbsp;методическими
                            материалами, общения с&nbsp;нашими методистами
                            и&nbsp;проведения пробных занятий с&nbsp;учениками
                            (обычно таких занятий не&nbsp;более 4-х).
                        </Text>

                        <Text as="p">Успехов!</Text>
                    </ContentWrapper>
                </PageWrapper>
                <CustomBreadCrumbs crumbs={crumbs} />
            </Container>

            <Footer />
        </Wrap>
    );
};

export default TeachersPage;
